import { useEffect } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'

/**
 * Small centered modal with backdrop. Escape and backdrop-click cancel.
 * `tone="danger"` switches the confirm button + icon to rose (for destructive ops).
 * `busy` (optional, 2026-10-18) — the request is running: both buttons disabled, the
 * confirm shows a spinner, and Esc / backdrop do nothing, so a second click can't send
 * it twice and the dialog can't vanish mid-delete. Callers that omit it are unchanged.
 */
export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'ยืนยัน',
  cancelLabel  = 'ยกเลิก',
  tone         = 'default',
  onConfirm,
  onCancel,
  busy = false,
}) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, busy])

  const confirmCls = tone === 'danger'
    ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-brand-600 hover:bg-brand-700 text-white'
  const iconCls = tone === 'danger'
    ? 'text-rose-600 bg-rose-50 ring-rose-100'
    : 'text-brand-600 bg-brand-50 ring-brand-100'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={busy ? undefined : onCancel} />
      <div className="relative w-full max-w-[400px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in">
        <div className="flex items-start gap-3">
          <span className={`w-9 h-9 rounded-full flex items-center justify-center ring-1 flex-none ${iconCls}`}>
            <AlertTriangle size={18} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[15.5px] font-semibold text-slate-900">{title}</div>
            {/* break-words: Thai runs have no spaces, so a long sentence has no
                break opportunity and overflows the panel instead of wrapping. */}
            {message && <div className="text-[14px] text-slate-600 mt-1 leading-relaxed break-words">{message}</div>}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold disabled:opacity-70 ${confirmCls}`}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
